/**
 * Data engine for the Overview onchain-activity widget.
 *
 * Wraps the shared `ApiClient` `useQuery` against the ephemeral blockchain feed
 * and selects its mode from the connected-wallet state (POST connected EVM
 * addresses, else GET the default preview chains). It exposes canonical rows +
 * multichain graph elements (via the shared transpose), and reconciles the two
 * "new activity" strategies the product wants:
 *
 *  - **interval** — refetch the page + graph on a cadence (works today, no infra)
 *  - **live**     — a `<StreamingProvider>` feeds rows via `setStreamedRows`
 *                   (merged ahead of the page with `mergeTransactionRows`), while
 *                   the page/graph still refresh on a slower cadence to stay fresh
 *  - **off**      — fetch once; refresh only on manual retry
 *
 * No bespoke fetch/transport/merge code — all of it comes from the lower packages.
 */
import * as React from "react";
import { useAddressInventory } from "@authe/web3";
import { mergeTransactionRows, type TxRow } from "@authe/tx-table";
import type { OverlayResolver } from "@authe/tx-graph";
import type { InteractionGraphDTO } from "@authe/fe-commons/models";
import type { LiveMode } from "@authe/fe-commons/display";
import { usePollLoop } from "@/app/pollLoop";
import type { TokenMetadataResult } from "@/features/tokens/useTokenMetadata";
import { useFeedGraphOverlays } from "./useFeedGraphOverlays";
import { useOnchainFeed } from "./useOnchainFeed";
import {
  ONCHAIN_REFRESH_MS,
  MAX_FEED_ROWS,
  PREVIEW_LIMIT,
  WALLET_LIMIT,
} from "./constants";

export interface UseOnchainActivityOptions {
  liveMode: LiveMode;
  /**
   * Resolve the graph's catalog overlays (labels, tags, risk). Default true.
   *
   * The panel passes `view === "graph"`: the Table tab draws none of it and has
   * its own row enrichment, so resolving there is a batched read — with a stored
   * ROW per address — that nothing on screen can show.
   */
  resolveOverlays?: boolean;
}

export interface OnchainActivityResult {
  rows: TxRow[];
  /**
   * The merged multichain graph as the BACKEND returns it, not React Flow
   * elements — the mapping happens inside the panel's lazily-loaded graph
   * section. See `useFeedGraphOverlays` for why this hook stops one step short.
   */
  graph: InteractionGraphDTO;
  /**
   * The catalog seam the graph was drawn through, or `undefined` when overlays
   * are off for this surface.
   *
   * Handed out so the panel can FREEZE it onto the saved view: an
   * `owner_snapshot` share link serves the owner's labels, entities and risk,
   * and by the time a view reaches the share dialog the resolver is gone. This
   * is the only place that can answer.
   */
  resolveOverlay: OverlayResolver | undefined;
  /**
   * Decimals + ticker for the tokens the graph's edges name.
   *
   * Handed out beside the overlay seam and for a related reason: the graph is
   * drawn by a lazily-loaded section, so whatever it is drawn WITH has to come
   * from here. Without it every edge on this panel was a line with no amount.
   */
  tokens: TokenMetadataResult;
  hasWallet: boolean;
  /** Connected wallet addresses the feed is querying (for the live-feed scope). */
  addresses: string[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  /** Lift newest-first streamed rows from `<OnchainLiveFeed>` (live mode only). */
  setStreamedRows: (rows: TxRow[]) => void;
  /** Count of streamed rows merged onto the page (drives the "N new" pill). */
  streamedCount: number;
  /** True when the feed reported more history behind its cursor. */
  hasMore: boolean;
  /** Fetch and APPEND the next page. */
  loadMore: () => void;
  isLoadingMore: boolean;
}

export function useOnchainActivity({
  liveMode,
  resolveOverlays = true,
}: UseOnchainActivityOptions): OnchainActivityResult {
  const { entries, isConnected } = useAddressInventory();
  const addresses = React.useMemo(
    () => Array.from(new Set(entries.map((e) => e.normalized).filter(Boolean))),
    [entries],
  );
  const hasWallet = isConnected && addresses.length > 0;
  const addressKey = addresses.join(",");

  const feed = useOnchainFeed({
    addresses: hasWallet ? addresses : [],
    limit: hasWallet ? WALLET_LIMIT : PREVIEW_LIMIT,
    graph: true,
  });

  const baseRows = feed.rows;
  // Across every loaded page, so the graph GROWS with "Load more" rather than
  // snapping back to the first page's view of the wallet — and drawn with the
  // reader's catalog, so a node here reads the same as it does everywhere else.
  //
  // `includeRow`, unlike the explorer's latest-blocks view, because this is the
  // surface a share link is minted on: an analyst's hand-set risk band lives on
  // the row and nowhere else, and it is the owner's own judgement that an
  // `owner_snapshot` link promises the recipient.
  const { graph, resolveOverlay, tokens } = useFeedGraphOverlays(feed.pages, {
    enabled: resolveOverlays,
    includeRow: true,
  });

  // Streamed rows lifted from <OnchainLiveFeed>. Reset when the query identity
  // changes (wallet connect/disconnect) or when leaving Live, so we never show
  // another context's rows or stale stream output.
  const [streamedRows, setStreamedRows] = React.useState<TxRow[]>([]);
  React.useEffect(() => {
    setStreamedRows([]);
  }, [addressKey, hasWallet]);
  React.useEffect(() => {
    if (liveMode !== "live") setStreamedRows([]);
  }, [liveMode]);

  const rows = React.useMemo(
    () => (streamedRows.length ? mergeTransactionRows(streamedRows, baseRows, MAX_FEED_ROWS) : baseRows),
    [streamedRows, baseRows],
  );

  // Auto-refresh: poll in interval mode; in live mode refresh the page/graph on a
  // slower cadence (the stream drives immediacy for the table between refetches).
  //
  // This refreshes every loaded page rather than collapsing back to the first,
  // so a reader who paged deliberately does not lose that on the next tick —
  // which makes a tick here the most expensive one in the app, and is why it
  // runs through the shared loop: a reader five pages deep no longer pays ten
  // reads a minute for a tab they are not looking at.
  const refetch = feed.refetch;
  usePollLoop({
    enabled: liveMode !== "off",
    intervalMs: liveMode === "live" ? ONCHAIN_REFRESH_MS * 4 : ONCHAIN_REFRESH_MS,
    onTick: refetch,
    failed: feed.error != null,
  });

  return {
    rows,
    graph,
    resolveOverlay,
    tokens,
    hasWallet,
    addresses,
    isLoading: feed.isLoading,
    isFetching: feed.isFetching,
    error: feed.error,
    refetch,
    setStreamedRows,
    streamedCount: streamedRows.length,
    hasMore: feed.hasMore,
    loadMore: feed.loadMore,
    isLoadingMore: feed.isLoadingMore,
  };
}
